import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, Sparkles } from 'lucide-react'
import { RenameField } from '@/components/features/rename-field'
import { suggestDocumentFilename } from '@/lib/api/documents'
import {
  buildPrefixedName,
  extractTodoIdFromFrontmatter,
  extractUniquenessSuffix,
  hasDatePrefix,
  hasTodoPrefix,
  slugifyTitle,
  stripKnownPrefixes,
  todayDatePrefix,
} from '@/lib/rename-filename'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

/** Result passed to onRename for file renames. */
export type RenameConfirmOptions = {
  title?: string
  /** When true, caller should not rename the file on disk. */
  skipFilename?: boolean
}

interface RenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'file' | 'folder'
  currentName: string
  documentTitle?: string
  documentId?: string | null
  frontmatter?: Record<string, unknown> | null
  pathFallback?: string | null
  onRename: (newName: string, options?: RenameConfirmOptions) => Promise<void>
}

export function RenameDialog({
  open,
  onOpenChange,
  type,
  currentName,
  documentTitle = '',
  documentId,
  frontmatter,
  pathFallback,
  onRename,
}: RenameDialogProps) {
  const todoId = type === 'file' ? extractTodoIdFromFrontmatter(frontmatter) : null
  const [slug, setSlug] = useState('')
  const [editedTitle, setEditedTitle] = useState('')
  const [updateTitle, setUpdateTitle] = useState(true)
  const [updateFilename, setUpdateFilename] = useState(true)
  const [dateOn, setDateOn] = useState(false)
  const [todoOn, setTodoOn] = useState(false)
  const [uniqOn, setUniqOn] = useState(false)
  const [uniqSuffix, setUniqSuffix] = useState<string | null>(null)
  const [dateLabel, setDateLabel] = useState(todayDatePrefix())
  const [isRenaming, setIsRenaming] = useState(false)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [avoidTitles, setAvoidTitles] = useState<string[]>([])
  const [avoidSlugs, setAvoidSlugs] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setSlug('')
      setEditedTitle('')
      setError(null)
      setIsRenaming(false)
      setIsSuggesting(false)
      setAvoidTitles([])
      setAvoidSlugs([])
      setUpdateTitle(true)
      setUpdateFilename(true)
      setUniqOn(false)
      setUniqSuffix(null)
      return
    }

    const displayName =
      type === 'file' && currentName.endsWith('.md')
        ? currentName.slice(0, -3)
        : currentName
    const extractedTodo = type === 'file' ? extractTodoIdFromFrontmatter(frontmatter) : null
    const extractedUniq =
      type === 'file' ? extractUniquenessSuffix(displayName, extractedTodo) : null
    setSlug(stripKnownPrefixes(displayName, extractedTodo))
    setEditedTitle(documentTitle || '')
    setDateOn(type === 'file' ? hasDatePrefix(displayName, extractedTodo) : false)
    setTodoOn(type === 'file' ? hasTodoPrefix(displayName, extractedTodo) : false)
    setUniqSuffix(extractedUniq)
    setUniqOn(Boolean(extractedUniq))
    setDateLabel(todayDatePrefix())
    setUpdateTitle(true)
    setUpdateFilename(true)
    setError(null)
    setAvoidTitles([])
    setAvoidSlugs([])
  }, [open, type, currentName, frontmatter, documentTitle])

  const newName =
    type === 'file'
      ? buildPrefixedName({
          slug,
          dateOn,
          todoOn,
          todoId,
          date: dateLabel,
          uniqOn,
          uniqSuffix,
        })
      : slug

  const handleFilenameChange = useCallback(
    (next: string) => {
      if (type === 'folder') {
        setSlug(next)
      } else {
        const nextUniq = extractUniquenessSuffix(next, todoId)
        if (nextUniq) {
          setUniqSuffix(nextUniq)
          setUniqOn(true)
        }
        setSlug(stripKnownPrefixes(next, todoId))
      }
      setError(null)
    },
    [type, todoId],
  )

  /** AI suggests Title Case title only (filename stays until Apply from title). */
  const handleSuggestTitle = useCallback(async () => {
    if (!documentId || type !== 'file') return
    setIsSuggesting(true)
    setError(null)
    try {
      const result = await suggestDocumentFilename(documentId, {
        avoid: avoidTitles,
        pathFallback,
      })
      const nextTitle = result.title?.trim() || ''
      if (!nextTitle) {
        toast.error('No title suggestion returned')
        return
      }
      setEditedTitle(nextTitle)
      setAvoidTitles((prev) => [...prev, nextTitle].slice(-8))
      if (!updateTitle) setUpdateTitle(true)
    } catch (err) {
      console.error('[RenameDialog] suggest failed:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to suggest title')
    } finally {
      setIsSuggesting(false)
    }
  }, [documentId, type, avoidTitles, pathFallback, updateTitle])

  const handleSuggestFilename = useCallback(async () => {
    if (!documentId || type !== 'file') return
    setIsSuggesting(true)
    setError(null)
    try {
      const result = await suggestDocumentFilename(documentId, {
        avoid: avoidSlugs,
        pathFallback,
      })
      const nextSlug = result.slug?.trim() || ''
      if (!nextSlug || nextSlug === 'untitled') {
        toast.error('No filename suggestion returned')
        return
      }
      setSlug(nextSlug)
      setAvoidSlugs((prev) => [...prev, nextSlug].slice(-8))
      if (!updateFilename) setUpdateFilename(true)
    } catch (err) {
      console.error('[RenameDialog] suggest filename failed:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to suggest filename')
    } finally {
      setIsSuggesting(false)
    }
  }, [documentId, type, avoidSlugs, pathFallback, updateFilename])

  const handleApplyTitleToFilename = useCallback(() => {
    const nextSlug = slugifyTitle(editedTitle)
    if (!nextSlug || nextSlug === 'untitled') {
      setError('Enter a document title before applying to filename')
      return
    }
    setSlug(nextSlug)
    if (!updateFilename) setUpdateFilename(true)
    setError(null)
  }, [editedTitle, updateFilename])

  const handleRename = async () => {
    if (type === 'folder') {
      if (!newName.trim()) {
        setError('Folder name cannot be empty')
        return
      }
      if (newName.trim().includes('/') || newName.trim().includes('\\')) {
        setError('Folder name cannot contain slashes')
        return
      }
    } else {
      if (!updateTitle && !updateFilename) {
        setError('Check at least one of title or filename')
        return
      }
      if (updateTitle && !editedTitle.trim()) {
        setError('Document title cannot be empty')
        return
      }
      if (updateFilename && !newName.trim()) {
        setError('Filename cannot be empty')
        return
      }
      if (updateFilename) {
        const finalName = newName.trim().endsWith('.md')
          ? newName.trim()
          : `${newName.trim()}.md`
        if (finalName.includes('/') || finalName.includes('..')) {
          setError('Filename cannot contain path separators')
          return
        }
      }
    }

    setIsRenaming(true)
    setError(null)

    try {
      if (type === 'folder') {
        await onRename(newName.trim())
      } else {
        const finalName =
          updateFilename
            ? newName.trim().endsWith('.md')
              ? newName.trim()
              : `${newName.trim()}.md`
            : currentName.endsWith('.md')
              ? currentName
              : `${currentName}.md`

        await onRename(finalName, {
          title: updateTitle ? editedTitle.trim() : undefined,
          skipFilename: !updateFilename,
        })
      }
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isRenaming && !isSuggesting) {
      e.preventDefault()
      void handleRename()
    } else if (e.key === 'Escape') {
      // Let Radix Dialog close on Escape; prevent the input from also trapping focus.
      e.preventDefault()
      onOpenChange(false)
    }
  }

  const currentBase =
    type === 'file' && currentName.endsWith('.md')
      ? currentName.slice(0, -3)
      : currentName

  const filenameChanged = newName.trim() !== currentBase
  const titleChanged = editedTitle.trim() !== (documentTitle || '').trim()

  const canSubmit =
    type === 'folder'
      ? Boolean(newName.trim()) && filenameChanged
      : ((updateTitle && titleChanged && Boolean(editedTitle.trim())) ||
          (updateFilename && filenameChanged && Boolean(newName.trim()))) &&
        (updateTitle || updateFilename)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Rename</DialogTitle>
          <DialogDescription>
            {type === 'file'
              ? 'Choose what to update. Suggest a Title Case title, then optionally apply it to the filename.'
              : 'Enter a new name for the folder.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 py-2">
          {type === 'file' && (
            <div
              className={cn(
                'grid gap-2 rounded-md border p-3',
                updateTitle ? 'border-border' : 'border-border/50 opacity-70',
              )}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rename-update-title"
                  checked={updateTitle}
                  onCheckedChange={(v) => setUpdateTitle(v === true)}
                  disabled={isRenaming}
                />
                <Label htmlFor="rename-update-title" className="text-sm font-medium cursor-pointer">
                  Document title
                </Label>
              </div>
              <div className="relative flex items-center">
                <Input
                  id="rename-doc-title"
                  value={editedTitle}
                  onChange={(e) => {
                    setEditedTitle(e.target.value)
                    setError(null)
                  }}
                  disabled={isRenaming || isSuggesting || !updateTitle}
                  onKeyDown={handleKeyDown}
                  placeholder="Title Case document title"
                  className="pr-10 text-sm"
                  autoFocus
                />
                {documentId ? (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void handleSuggestTitle()}
                    disabled={isRenaming || isSuggesting || !updateTitle}
                    title="AI suggest title (click again to shuffle)"
                    aria-label="AI suggest title"
                    className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                  >
                    {isSuggesting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </button>
                ) : null}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Shown in tabs, browse, and search. Sparkle suggests Title Case only.
              </p>
            </div>
          )}

          <div
            className={cn(
              'grid gap-2 rounded-md border p-3',
              type === 'file' && !updateFilename ? 'border-border/50 opacity-70' : 'border-border',
            )}
          >
            {type === 'file' ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rename-update-filename"
                    checked={updateFilename}
                    onCheckedChange={(v) => setUpdateFilename(v === true)}
                    disabled={isRenaming}
                  />
                  <Label
                    htmlFor="rename-update-filename"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Filename
                  </Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={handleApplyTitleToFilename}
                  disabled={isRenaming || isSuggesting || !editedTitle.trim()}
                >
                  Apply from title
                </Button>
              </div>
            ) : (
              <Label htmlFor="rename-input" className="text-sm">
                Folder name
              </Label>
            )}
            <RenameField
              id="rename-input"
              value={newName}
              onChange={handleFilenameChange}
              showSuggest={type === 'file' && Boolean(documentId)}
              onSuggest={handleSuggestFilename}
              isSuggesting={isSuggesting}
              suggestLabel="AI suggest filename (click again to shuffle)"
              showDateChip={type === 'file'}
              dateOn={dateOn}
              dateLabel={dateLabel}
              onDateToggle={() => setDateOn((v) => !v)}
              todoId={todoId}
              todoOn={todoOn}
              onTodoToggle={() => setTodoOn((v) => !v)}
              uniqSuffix={type === 'file' ? uniqSuffix : null}
              uniqOn={uniqOn}
              onUniqToggle={() => setUniqOn((v) => !v)}
              autoFocus={type === 'folder'}
              disabled={isRenaming || (type === 'file' && !updateFilename)}
              onKeyDown={handleKeyDown}
            />
            {type === 'file' ? (
              <p className="text-[11px] text-muted-foreground">
                Sparkle suggests the slug body. Date / Basecamp / uniqueness chips stay separate.
                Or use Apply from title.
              </p>
            ) : null}
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleRename()}
            disabled={isRenaming || isSuggesting || !canSubmit}
          >
            {isRenaming ? 'Saving...' : 'Rename'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
